(ns ui.sticker-dialogs.ui
  "The hiccup pieces the Replicant sticker dialogs share: the sticker
   title in the Instagram look, design and color choosers, text fields
   with an emoji button, chips of recent entries and the buttons."
  (:require [clojure.string :as str]
            [storrito.ui.button :as button]
            [ui.sticker-dialogs.model :as model]))

(def instagram-font
  "Instagram Sans Condensed")

(defn gradient-css
  "A left-to-right CSS gradient over the `gradient` map of a sticker
   design (`{\"0\" color \"0.5\" color \"1\" color}`), all stops in order."
  [gradient]
  (str "linear-gradient(90deg, "
       (str/join ", "
                 (map val
                      (sort-by (comp js/parseFloat str key)
                               gradient)))
       ")"))

(defn gradient-text-style
  "Text in a gradient: `from` and `to` colors, or a whole design gradient map."
  ([gradient]
   {:-webkit-background-clip "text"
    :-webkit-text-fill-color "transparent"
    :background-image (gradient-css gradient)})
  ([from to]
   (gradient-text-style {"0" from
                         "1" to})))

(defn title
  "The big sticker name above a dialog, e.g. '#HASHTAG' in its gradient."
  [text style]
  [:div.text-4xl.mb-2
   [:span {:style (merge {:font-family instagram-font}
                         style)}
    text]])

(defn design-entry
  "A preview of a sticker `design` (see `storrito.editor.sticker-design`)
   with the sample `text`, as a selectable chooser entry."
  [{:keys [text design active? actions]}]
  (let [text-props (:text-props design)]
    [:div.bg-gray-100.shadow.notranslate.flex.justify-center.items-center.cursor-pointer.px-2.rounded
     {:class (concat ["sm:rounded-md"]
                     (when active?
                       ["border" "border-2" "border-gray-500"]))
      :style {:background-color (:bg-color design)}
      :on {:click actions}}
     [:div
      {:style (merge {:font-size "30px"
                      :font-family (:fontFamily text-props instagram-font)
                      :color (:fill text-props)}
                     (when-let [gradient (:gradient design)]
                       (gradient-text-style gradient)))}
      text]]))

(defn color-palette
  "The color swatches of a sticker; `action-fn` maps a color to the
   actions of its click."
  [{:keys [colors value action-fn]}]
  [:div.flex.gap-1.my-4.flex-wrap
   (map (fn [color]
          [:div.w-8.h-8.cursor-pointer.rounded.border-2
           {:replicant/key color
            :class (if (= value color)
                     ["border-gray-500"]
                     ["border-gray-200"])
            :style {:background-color color}
            :on {:click (action-fn color)}}])
        colors)])

(defn emoji-button
  "Opens the emoji picker for the text field `dom-id`, whose value is
   the dialog `field`. The font size of `class` is the size of the icon:
   the button is exactly as big as its icon (Font Awesome's `fa-lg`
   would collapse its line height to almost nothing), so that it can be
   positioned inside of a text field."
  [{:keys [field dom-id class style]}]
  [:button.cursor-pointer.inline-flex.items-center.justify-center.leading-none
   {:type "button"
    :title "Select an emoji"
    :tabindex -1
    :class (or class ["text-black" "text-xl" "m-2"])
    :style style
    :on {:click [[:js/prevent-default]
                 [:sticker-dialog/emoji-open field dom-id]]}}
   [:i.far.fa-smile]])

(def emoji-button-inside-css
  "Places the `emoji-button` in the bottom right corner of its text
   field (the host element needs `relative` positioning)."
  ["absolute" "right-2" "bottom-2" "text-xl"])

(defn text-field
  "A text field bound to the dialog `field`: typing runs through
   `ui.sticker-dialogs.model/set-field`."
  [state {:keys [field dom-id tag placeholder rows class style autofocus?]
          :or {tag :input}}]
  (let [value (or (model/field-value state field) "")]
    [tag
     (cond-> {:id dom-id
              :value value
              :placeholder placeholder
              :class class
              :style style
              :autocomplete "off"
              :on {:input [[:sticker-dialog/set-field field :event/target.value]]}}
       (= tag :input) (assoc :type "text")
       rows (assoc :rows rows)
       autofocus? (assoc :replicant/on-mount (fn [{:keys [replicant/node]}]
                                                (.focus node))))]))

(defn chip
  "A rounded chip of a recent entry with an optional remove button."
  [{:keys [label sublabel title actions remove-actions remove-title hover-color selected? disabled?]}]
  (let [hover (or hover-color "orange")]
    [:span.inline-flex.items-center.overflow-hidden.rounded-full.border.text-sm.font-medium.shadow-sm.transition-all
     {:class (concat [(str "hover:border-" hover "-400")
                      (str "hover:bg-" hover "-50")
                      (str "hover:text-" hover "-800")
                      "hover:shadow-lg"]
                     (cond
                       selected? [(str "border-" hover "-400") (str "bg-" hover "-100") (str "text-" hover "-900") "shadow-md"]
                       disabled? ["border-gray-200" "bg-gray-50" "text-gray-400"]
                       :else ["border-gray-300" "bg-white" "text-gray-800"]))}
     [:button.px-4.py-2.text-left.transition-colors
      {:type "button"
       :class [(str "hover:bg-" hover "-200") (str "hover:text-" hover "-900")]
       :disabled disabled?
       :title title
       :on {:click (into [[:js/prevent-default]] actions)}}
      [:span.block.leading-tight label]
      (when (seq sublabel)
        [:span.block.max-w-xs.truncate.text-xs.font-normal.text-gray-500
         sublabel])]
     (when remove-actions
       [:button.border-l.border-gray-200.px-3.py-2.text-gray-500.transition-colors.hover:bg-red-100.hover:text-red-700
        {:type "button"
         :title remove-title
         :aria-label remove-title
         :on {:click (into [[:js/prevent-default] [:js/stop-propagation]] remove-actions)}}
        "×"])]))

(def button-base
  (concat button/css-default
          button/css-md))

(def primary-css
  button/button-primary-css)

(def secondary-colors
  button/css-secondary)

(def secondary-css
  button/button-secondary-css)

(def warning-css
  (concat button/css-default
          button/css-warning
          button/css-md))

(defn primary-button
  [attrs & content]
  (into [:button (merge {:type "button"
                         :class button/button-primary-css}
                        attrs)]
        content))

(defn cancel-button
  []
  [:button {:type "button"
            :class (concat button/button-cancel-css ["mr-4"])
            :on {:click [[:ui/modal-close]]}}
   "Cancel"])

(def instagram-note
  "*The presentation on Storrito may vary from the final post on Instagram.")

(defn error-text
  [message]
  [:div.font-medium.text-sm.mt-2.h-6.text-red-600
   (or message "")])

(defn need-to-connect
  "Shown instead of a dialog that needs a connected Instagram account."
  []
  [:div.flex.flex-col.mt-4
   [:p.mb-4.font-medium
    "You need to connect an Instagram account"
    " to add this sticker to your stories."]
   [:div.flex.justify-between.mt-4
    [:button {:type "button"
              :class button/button-secondary-css
              :on {:click [[:ui/modal-close]]}}
     "Cancel"]
    [:a {:class button/button-primary-css
         :href (str "/ui/connect3/" (random-uuid))
         :target "_blank"}
     "Connect your Instagram Account"]]])

(defn blank?
  [s]
  (str/blank? (str s)))
